import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import UserContext from '../../context/UserContext';
import PersonControl from '../Common/PersonControl';
import WhoseHeadControl from '../Common/WhoseHeadControl';
import PlotTrackScenePeople from './PlotTrackScenePeople';
import { allowedStatesForRow, chekhovVisibilityCopy, isChekhovRow, whoseHeadValue } from '../../utils/plottrackGrid';
import {
  ABSENT_CELL_BODY,
  ABSENT_CELL_CONFIRM,
  ABSENT_CELL_TITLE,
  CHEKHOV_SETUP_PAYOFF_HINT,
  DRAFT_NEED_SCENE,
  DRAFT_PACK_HINT,
  DRAFT_THIS_SCENE,
  GAP_PURPOSE_BLOCK,
  GAP_PURPOSE_FIELD,
  GAP_SAVE_NAME,
  GAP_SAVE_PURPOSE,
  GAP_SCENE_NAME,
  GRAIN_SEQUENCE,
  panelStorylineLabel,
  PERSON_SAVE_FAILED,
  SAVED,
  SCENE_EDIT_FAILED,
  SCENE_EDIT_LABEL,
  SCENE_EDIT_NEEDS_TITLE,
  SCENE_EDIT_PLACE,
  SCENE_EDIT_PLACE_AFTER,
  SCENE_EDIT_PLACE_END,
  SCENE_EDIT_PLACE_START,
  SCENE_EDIT_PLACE_START_OF,
  SCENE_EDIT_PLACE_STAY,
  SCENE_EDIT_SAVE,
  SCENE_EDIT_SEQUENCE,
  SCENE_EDIT_TITLE,
  SCENE_MENU_KEEP,
  SUBPLOT_STATES_HINT,
} from '../../utils/writerCopy';

const CELL_STATE_LABEL = {
  active: 'Active',
  quiet: 'Quiet',
  setup: 'Setup',
  payoff: 'Payoff',
  empty: 'Blank',
  absent: 'Absent',
};

function draftHasContent(note, beats) {
  if (String(note || '').trim()) return true;
  return (beats || []).some((beat) => String(beat || '').trim());
}

/** The sequence header whose scenes include this scene, or null. */
export function sequenceOfScene(sequences, sceneId) {
  if (!sceneId) return null;
  return (sequences || []).find((header) => (
    (header?.scene_ids || []).some((id) => String(id) === String(sceneId))
  )) || null;
}

const PLACE_STAY = 'stay';
const PLACE_START = 'start';
const PLACE_END = 'end';

/**
 * Run 51: where the scene can go in a sequence. Its own sequence offers
 * "Where it is now" first; another sequence defaults to the end.
 */
export function placeOptions(sequence, sceneId, sceneTitles = {}, sameSequence = false) {
  const others = (sequence?.scene_ids || []).filter((id) => String(id) !== String(sceneId));
  const options = [];
  if (sameSequence) options.push({ value: PLACE_STAY, label: SCENE_EDIT_PLACE_STAY });
  if (others.length) {
    options.push({ value: PLACE_START, label: SCENE_EDIT_PLACE_START });
    others.forEach((id) => {
      options.push({
        value: `after:${id}`,
        label: SCENE_EDIT_PLACE_AFTER(sceneTitles[String(id)] || 'Untitled scene'),
      });
    });
  }
  if (!sameSequence) options.push({ value: PLACE_END, label: SCENE_EDIT_PLACE_END });
  return options;
}

/** Run 48: rename a scene, or move it to another sequence; run 51: and choose where. */
/** Run 60: spots in every other sequence, for "Goes". Values carry the sequence. */
export function otherSequencePlaces(named, current, sceneId, sceneTitles = {}) {
  return (named || [])
    .filter((header) => String(header.id) !== String(current?.id || ''))
    .map((header) => {
      const ids = (header.scene_ids || []).filter((id) => String(id) !== String(sceneId));
      const options = [{ value: `start:${header.id}`, label: SCENE_EDIT_PLACE_START_OF(header.display_name) }];
      ids.forEach((id) => options.push({
        value: `after:${header.id}:${id}`,
        label: SCENE_EDIT_PLACE_AFTER(sceneTitles[String(id)] || 'Untitled scene'),
      }));
      return { id: String(header.id), name: header.display_name, options };
    });
}

/** "start:<seq>" or "after:<seq>:<scene>" from otherSequencePlaces; null otherwise. */
export function crossPlace(value) {
  const parts = String(value || '').split(':');
  if (parts[0] === 'start' && parts.length === 2) {
    return { sequenceId: parts[1], afterSceneId: undefined, atStart: true };
  }
  if (parts[0] === 'after' && parts.length === 3) {
    return { sequenceId: parts[1], afterSceneId: parts[2], atStart: false };
  }
  return null;
}

/** Beat rows are lines. A string stays one line, so it cannot become one row per letter. */
export function beatDraftLines(beats) {
  let lines;
  if (Array.isArray(beats)) {
    lines = beats.map((beat) => (
      typeof beat === 'string' ? beat : String(beat?.action || beat?.text || '')
    ));
  } else if (typeof beats === 'string' && beats !== '') {
    lines = [beats];
  } else {
    lines = [];
  }
  return lines.length ? lines : [''];
}

function sameLines(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((line, index) => line === right[index]);
}

function SceneEdit({
  sceneId, title: shownTitle, sequences, sceneTitles, onEditScene, beats = [], rowId, columnId,
}) {
  const current = sequenceOfScene(sequences, sceneId);
  const named = (sequences || []).filter((header) => header?.id && header?.display_name);
  const [title, setTitle] = useState(shownTitle || '');
  const [sequenceId, setSequenceId] = useState(current?.id ? String(current.id) : '');
  const [place, setPlace] = useState(PLACE_STAY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // After a save the board reloads with the new title and sequence; keep "Saved." until
  // the writer picks another scene.
  useEffect(() => {
    setError(null);
    setSaved(false);
  }, [sceneId]);

  useEffect(() => {
    setTitle(shownTitle || '');
    setSequenceId(current?.id ? String(current.id) : '');
    setPlace(PLACE_STAY);
  }, [sceneId, shownTitle, current?.id]);

  const moving = Boolean(sequenceId) && sequenceId !== String(current?.id || '');
  const targetSequence = named.find((header) => String(header.id) === sequenceId) || null;
  const places = targetSequence
    ? placeOptions(targetSequence, sceneId, sceneTitles, !moving)
    : [];
  // Run 60: "Goes" listed only the scene's own sequence, so a move elsewhere
  // meant finding Sequence first. Other sequences' spots are listed too.
  const elsewhere = moving ? [] : otherSequencePlaces(named, current, sceneId, sceneTitles);

  const save = async () => {
    const name = title.trim();
    if (!name) {
      setError(SCENE_EDIT_NEEDS_TITLE);
      return;
    }
    const cross = crossPlace(place);
    const after = cross ? cross.afterSceneId : (place.startsWith('after:') ? place.slice('after:'.length) : undefined);
    const atStart = cross ? cross.atStart : place === PLACE_START;
    const targetId = cross ? cross.sequenceId : (moving ? sequenceId : undefined);
    const sentTitle = name === (shownTitle || '') ? undefined : name;
    const sentBeats = (beats || []).map((beat) => String(beat).trim()).filter(Boolean);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const saved = await onEditScene({
        sceneId,
        title: sentTitle,
        sequenceId: targetId,
        afterSceneId: after,
        atStart: atStart ? true : undefined,
        beats: sentBeats,
        rowId,
        columnId,
      });
      // Saved. only after the server echoes every field this click sent.
      if (sentTitle !== undefined && saved?.title !== sentTitle) {
        throw new Error(SCENE_EDIT_FAILED);
      }
      if (targetId && String(saved?.sequence_id || '') !== String(targetId)) {
        throw new Error(SCENE_EDIT_FAILED);
      }
      if (!sameLines(saved?.beats, sentBeats)) {
        throw new Error(SCENE_EDIT_FAILED);
      }
      setSaved(true);
      setPlace(PLACE_STAY);
    } catch (err) {
      setError(err?.message || SCENE_EDIT_FAILED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="pt-inspector__scene" data-testid="pt-scene-edit">
      <legend>{SCENE_EDIT_LABEL}</legend>
      <label className="pt-inspector__field" htmlFor="pt-scene-title">
        <span>{SCENE_EDIT_TITLE}</span>
        <input
          id="pt-scene-title"
          value={title}
          maxLength={255}
          onChange={(event) => { setTitle(event.target.value); setSaved(false); }}
          disabled={busy}
        />
      </label>
      {named.length > 1 && (
        <label className="pt-inspector__field" htmlFor="pt-scene-sequence">
          <span>{SCENE_EDIT_SEQUENCE}</span>
          <select
            id="pt-scene-sequence"
            value={sequenceId}
            onChange={(event) => {
              const next = event.target.value;
              setSequenceId(next);
              setPlace(next === String(current?.id || '') ? PLACE_STAY : PLACE_END);
              setSaved(false);
            }}
            disabled={busy}
          >
            {!current && <option value="">—</option>}
            {named.map((header) => (
              <option key={header.id} value={String(header.id)}>{header.display_name}</option>
            ))}
          </select>
        </label>
      )}
      {(places.length > 1 || elsewhere.length > 0) && (
        <label className="pt-inspector__field" htmlFor="pt-scene-place">
          <span>{SCENE_EDIT_PLACE}</span>
          <select
            id="pt-scene-place"
            value={place}
            onChange={(event) => { setPlace(event.target.value); setSaved(false); }}
            disabled={busy}
          >
            {elsewhere.length > 0 && targetSequence ? (
              <>
                {places.filter((option) => option.value === PLACE_STAY).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
                <optgroup label={targetSequence.display_name}>
                  {places.filter((option) => option.value !== PLACE_STAY).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value === PLACE_START ? SCENE_EDIT_PLACE_START_OF(targetSequence.display_name) : option.label}
                    </option>
                  ))}
                </optgroup>
                {elsewhere.map((group) => (
                  <optgroup key={group.id} label={group.name}>
                    {group.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </optgroup>
                ))}
              </>
            ) : places.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      )}
      <button type="button" className="pt-inspector__save-scene" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : SCENE_EDIT_SAVE}
      </button>
      {saved && <span className="pt-inspector__saved" role="status">{SAVED}</span>}
      {error && <p className="pt-inspector__error">{error}</p>}
    </fieldset>
  );
}

export default function PlotTrackInspector({
  selection,
  onClose,
  onSave,
  onDraftScene,
  onSetWhoseHead,
  onSetPerson,
  people = [],
  castIds = null,
  showWhoseHead = true,
  showPerson = true,
  sequenceSized = false,
  columnTitle = '',
  draftGate = false,
  importedScene = null,
  onSavePurpose,
  onRenameScene,
  onEditScene,
  sequences = [],
  sceneTitles = {},
  scenePeople = null,
  peoplePool = [],
  onSetScenePerson,
}) {
  const shell = useContext(UserContext);
  // Alone in a test, the inspector keeps the control. In the signed-in
  // shell it waits until /users/me says the cell check accepts absent.
  const absentAllowed = shell == null || shell.plotGridAbsent === true;
  const row = selection?.row;
  const column = selection?.column;
  const cell = selection?.cell;
  const [state, setState] = useState(cell?.state || 'empty');
  const [note, setNote] = useState(cell?.note || '');
  const [beats, setBeats] = useState(beatDraftLines(cell?.beats));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [personError, setPersonError] = useState(null);
  const [sceneName, setSceneName] = useState(importedScene?.name || '');
  const [scenePurpose, setScenePurpose] = useState(importedScene?.purpose || '');
  const [settleError, setSettleError] = useState(null);
  const [settling, setSettling] = useState(false);
  const [cellSaved, setCellSaved] = useState(false);
  const [absentConfirm, setAbsentConfirm] = useState(false);
  // #1710: a new cell object (POV, when, people, a board refresh) must not
  // wipe a note, beat, or state the writer has typed and not saved.
  const fieldsDirty = useRef(false);
  const noteRef = useRef(null);
  const restoreNoteFocus = useRef(false);
  const noteCaret = useRef(null);
  // #2046: a refresh can hand the same cell back with a numeric id one
  // time and a string id the next. Those are the same cell. Resetting on
  // the raw id dropped the letters after the first one and moved focus.
  const selectionKey = `${String(row?.id ?? '')}:${String(column?.id ?? '')}`;

  useEffect(() => {
    fieldsDirty.current = false;
    restoreNoteFocus.current = false;
    setState(cell?.state || 'empty');
    setNote(cell?.note || '');
    setBeats(beatDraftLines(cell?.beats));
    setError(null);
    setPersonError(null);
    setCellSaved(false);
    setAbsentConfirm(false);
  }, [selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fieldsDirty.current) return;
    setState(cell?.state || 'empty');
    setNote(cell?.note || '');
    setBeats(beatDraftLines(cell?.beats));
  }, [cell]);

  const restoreNoteCaret = () => {
    if (!restoreNoteFocus.current) return;
    const node = noteRef.current;
    if (!node) return;
    if (document.activeElement !== node) node.focus();
    const caret = noteCaret.current;
    if (document.activeElement === node && caret != null) {
      node.setSelectionRange(caret, caret);
    }
  };

  useLayoutEffect(() => {
    restoreNoteCaret();
  });

  useEffect(() => {
    restoreNoteCaret();
    restoreNoteFocus.current = false;
  });

  useEffect(() => {
    setSceneName(importedScene?.name || '');
    setScenePurpose(importedScene?.purpose || '');
    setSettleError(null);
  }, [importedScene?.id, importedScene?.name, importedScene?.purpose]);

  if (!selection) return null;

  const states = allowedStatesForRow(row?.type).filter((value) => (
    value !== 'absent' || absentAllowed || state === 'absent'
  ));
  const chekhov = isChekhovRow(row);
  const purposeBlocksDraft = Boolean(importedScene && importedScene.purpose_settled === false);
  const canDraft = Boolean(column?.scene_id && onDraftScene) && !purposeBlocksDraft;

  const saveName = async () => {
    if (!importedScene?.id || !onRenameScene) return;
    setSettling(true);
    setSettleError(null);
    try {
      await onRenameScene(importedScene.id, sceneName);
    } catch (err) {
      setSettleError(err?.message || 'Could not update the scene name.');
    } finally {
      setSettling(false);
    }
  };

  const savePurpose = async () => {
    if (!importedScene?.id || !onSavePurpose) return;
    setSettling(true);
    setSettleError(null);
    try {
      await onSavePurpose(importedScene.id, scenePurpose);
    } catch (err) {
      setSettleError(err?.message || 'Could not update what this scene does.');
    } finally {
      setSettling(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setCellSaved(false);
    const markingAbsent = state === 'absent';
    try {
      await onSave({
        rowId: row.id,
        columnId: column.id,
        state,
        note: markingAbsent ? '' : note,
        beats: markingAbsent ? [] : beats.map((beat) => beat.trim()).filter(Boolean),
      });
      fieldsDirty.current = false;
      setCellSaved(true);
    } catch (err) {
      setError(err?.message || 'Could not save this cell.');
    } finally {
      setSaving(false);
    }
  };

  // Marking absent clears the note and the beats on the server. Save in the
  // same step so the grid does not keep painting them.
  const commitAbsent = async () => {
    const previous = { state, note, beats: [...beats] };
    fieldsDirty.current = true;
    setAbsentConfirm(false);
    setState('absent');
    setNote('');
    setBeats(['']);
    setSaving(true);
    setError(null);
    setCellSaved(false);
    try {
      await onSave({
        rowId: row.id,
        columnId: column.id,
        state: 'absent',
        note: '',
        beats: [],
      });
      fieldsDirty.current = false;
      setCellSaved(true);
    } catch (err) {
      setState(previous.state);
      setNote(previous.note);
      setBeats(previous.beats.length ? previous.beats : ['']);
      setError(err?.message || 'Could not save this cell.');
    } finally {
      setSaving(false);
    }
  };

  const onStateChange = (value) => {
    if (value === 'absent' && !absentAllowed) return;
    if (value === 'absent' && state !== 'absent') {
      if (draftHasContent(note, beats)) {
        setAbsentConfirm(true);
        return;
      }
      void commitAbsent();
      return;
    }
    fieldsDirty.current = true;
    setAbsentConfirm(false);
    setState(value);
  };

  return (
    <aside className="pt-inspector" aria-label="Cell inspector">
      <header className="pt-inspector__header">
        <div>
          <p className="pt-inspector__row">{panelStorylineLabel(row)}</p>
          {sequenceSized && <p className="pt-inspector__grain">{GRAIN_SEQUENCE}</p>}
          <h2 className="pt-inspector__title">{columnTitle || column?.display_name || 'Untitled scene'}</h2>
        </div>
        <button type="button" className="pt-inspector__close" onClick={onClose}>
          Close
        </button>
      </header>

      {column?.scene_id && onEditScene && !importedScene && (
        <SceneEdit
          sceneId={column.scene_id}
          title={columnTitle || column?.display_name || ''}
          sequences={sequences}
          sceneTitles={sceneTitles}
          onEditScene={onEditScene}
          beats={beats}
          rowId={row?.id}
          columnId={column?.id}
        />
      )}

      {scenePeople && onSetScenePerson && (
        <PlotTrackScenePeople people={scenePeople} pool={peoplePool} onSet={onSetScenePerson} />
      )}

      <label className="pt-inspector__field" htmlFor="pt-cell-state">
        <span>State</span>
        <select
          id="pt-cell-state"
          value={state}
          disabled={saving}
          onChange={(e) => onStateChange(e.target.value)}
        >
          {states.map((value) => (
            <option key={value} value={value}>{CELL_STATE_LABEL[value] || value}</option>
          ))}
        </select>
      </label>
      {absentConfirm && (
        <div className="pt-inspector__confirm" role="dialog" aria-labelledby="pt-absent-title">
          <h3 id="pt-absent-title">{ABSENT_CELL_TITLE}</h3>
          <p>{ABSENT_CELL_BODY}</p>
          <button type="button" onClick={() => setAbsentConfirm(false)}>{SCENE_MENU_KEEP}</button>
          <button type="button" onClick={() => { void commitAbsent(); }}>{ABSENT_CELL_CONFIRM}</button>
        </div>
      )}
      {state === 'absent' && <div className="pt-inspector__absent pt-cell pt-cell--absent" aria-hidden="true" />}
      {chekhov && (
        <p className="pt-inspector__hint">
          {chekhovVisibilityCopy(row.visibility).label} — {chekhovVisibilityCopy(row.visibility).hint}. {CHEKHOV_SETUP_PAYOFF_HINT}
        </p>
      )}
      {!chekhov && (
        <p className="pt-inspector__hint">{SUBPLOT_STATES_HINT}</p>
      )}

      {state !== 'absent' && (
      <div className="pt-inspector__field">
        <label htmlFor="pt-cell-note">Note</label>
        <textarea
          id="pt-cell-note"
          ref={noteRef}
          value={note}
          autoComplete="off"
          onChange={(e) => {
            fieldsDirty.current = true;
            restoreNoteFocus.current = true;
            noteCaret.current = e.target.selectionStart;
            setNote(e.target.value);
          }}
          rows={4}
        />
      </div>
      )}

      {state !== 'absent' && (
      <div className="pt-inspector__field">
        <span>Moments</span>
        {beats.map((beat, index) => (
          <div key={index} className="pt-inspector__beat">
            <input
              value={beat}
              onChange={(e) => {
                fieldsDirty.current = true;
                const next = [...beats];
                next[index] = e.target.value;
                setBeats(next);
              }}
              placeholder="Present-tense action"
            />
            <button
              type="button"
              onClick={() => { fieldsDirty.current = true; setBeats(beats.filter((_, i) => i !== index)); }}
              aria-label={`Remove moment ${index + 1}`}
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" onClick={() => { fieldsDirty.current = true; setBeats([...beats, '']); }}>
          Add moment
        </button>
      </div>
      )}

      {((showWhoseHead && onSetWhoseHead) || (showPerson && onSetPerson)) && column?.scene_id && (
        <div className="pt-inspector__heads">
          {showWhoseHead && onSetWhoseHead && (
            <WhoseHeadControl
              id="pt-whose-head-scene"
              value={whoseHeadValue(column)}
              people={people}
              castIds={castIds}
              onChange={(next) => onSetWhoseHead(column.scene_id, next)}
            />
          )}
          {showPerson && onSetPerson && (
            <PersonControl
              id="pt-person-scene"
              person={column.person}
              onChange={async (mode) => {
                setPersonError(null);
                try {
                  await onSetPerson(column.scene_id, mode);
                } catch (err) {
                  setPersonError(err?.message || PERSON_SAVE_FAILED);
                }
              }}
            />
          )}
        </div>
      )}
      {personError && <p className="pt-inspector__error">{personError}</p>}

      {importedScene ? (
        <div data-testid="import-scene-settle">
          <label className="pt-inspector__field" htmlFor="pt-import-name">
            <span>{GAP_SCENE_NAME}</span>
            <input
              id="pt-import-name"
              value={sceneName}
              onChange={(event) => setSceneName(event.target.value)}
            />
          </label>
          <button type="button" onClick={saveName} disabled={settling || !onRenameScene}>
            {GAP_SAVE_NAME}
          </button>
          <label className="pt-inspector__field" htmlFor="pt-import-purpose">
            <span>{GAP_PURPOSE_FIELD}</span>
            <textarea
              id="pt-import-purpose"
              value={scenePurpose}
              rows={3}
              onChange={(event) => setScenePurpose(event.target.value)}
            />
          </label>
          <button type="button" onClick={savePurpose} disabled={settling || !onSavePurpose}>
            {GAP_SAVE_PURPOSE}
          </button>
          {settleError ? <p className="pt-inspector__error">{settleError}</p> : null}
        </div>
      ) : null}

      {error && <p className="pt-inspector__error">{error}</p>}
      {column?.scene_id && (
      <div className="pt-inspector__draft">
        <button
          type="button"
          className="pt-inspector__draft-btn"
          onClick={onDraftScene}
          disabled={!canDraft}
        >
          {DRAFT_THIS_SCENE}
        </button>
        {purposeBlocksDraft && (
          <span className="pt-inspector__why" data-testid="draft-purpose-block">{GAP_PURPOSE_BLOCK}</span>
        )}
        {!canDraft && !purposeBlocksDraft && (
          <span className="pt-inspector__why">{DRAFT_NEED_SCENE}</span>
        )}
        {canDraft && draftGate && (
          <span className="pt-inspector__why" data-testid="draft-pack-hint">{DRAFT_PACK_HINT}</span>
        )}
      </div>
      )}
      <button type="button" className="pt-inspector__save" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save cell'}
      </button>
      {cellSaved && <span className="pt-inspector__saved" role="status">{SAVED}</span>}
    </aside>
  );
}
